// The anaesthetist's PAC worklist (PRD B2).
//
// Until this existed a clearance could only be recorded from inside a case,
// so an anaesthetist had to already know which case to open. The three tabs
// are three SERVER-side queries rather than one result sliced up here -- see
// the note at the top of OtCases.js for why a browser-side filter over a
// truncated page is a lie.

import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Chip } from "@mui/material";

import { IpdDataGrid, IpdTabBar } from "../../ipd/utils";
import { formatIstDateTime, istDayKey } from "../../../utils/istDateTime";
import { fetchPacQueue } from "../api/otApi";
import OtScreen from "../components/OtScreen";
import PreAnesthesiaAssessmentModal from "../components/PreAnesthesiaAssessmentModal";
import { LifecycleChip, errorMessage } from "../components/common";
import { OT_BASE } from "../constants";
import { isReviewDue } from "../utils/pacQueue";

const TABS = [
  { id: "mine", label: "My PAC Queue", params: { mine: true } },
  // Never assigned, or assigned as a typed name only. Nothing is backfilled,
  // so without this tab every case created before the staff picker would be
  // on nobody's queue.
  { id: "unassigned", label: "Unassigned Cases", params: { unassigned: true } },
  { id: "all", label: "All Scheduled Cases", params: {} },
];

export default function OtPacQueue() {
  const history = useHistory();
  const [tab, setTab] = useState("mine");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pacRow, setPacRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetchPacQueue(TABS.find((t) => t.id === tab).params);
      setRows(res?.data || []);
    } catch (err) {
      setError(errorMessage(err, "Could not load the PAC queue"));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  // A request row carries the REQUEST's id, which can equal another
  // patient's case id. Its own case, once the PAC has opened one, is
  // case_id; without one there is no case page, so the PAC is recorded here.
  const openRow = (row) => {
    if (!row.is_request) {
      history.push(`${OT_BASE}/case/${row.id}`);
    } else if (row.case_id) {
      history.push(`${OT_BASE}/case/${row.case_id}`);
    } else {
      setPacRow(row);
    }
  };

  return (
    <OtScreen
      title="Pre-Anesthesia Clinic (PAC) Worklist"
      description="Surgical patients prioritized for pre-anesthetic clinical assessment and surgical fitness clearance."
      loading={loading}
      error={error}
      // The tabs are inside OtScreen, so the error branch replaces them too:
      // without a retry a transient failure strands the user here, and
      // re-clicking the same sidebar link does not remount a v5 route.
      onRetry={load}
    >
      <IpdTabBar
        tabs={TABS.map((t) => ({ id: t.id, label: t.label }))}
        activeTab={tab}
        onChange={setTab}
        ariaLabel="PAC queue"
      />
      <IpdDataGrid
        rows={rows}
        emptyMessage="No patients currently awaiting pre-anesthesia clearance"
        onRowClick={(p) => openRow(p.row)}
        columns={[
          { field: "patient_name", headerName: "Patient", flex: 1.2, sortable: false },
          { field: "case_number", headerName: "Case", width: 140, sortable: false },
          {
            field: "next_booking_start",
            headerName: "Surgery",
            width: 190,
            sortable: false,
            // "Not booked" is a real state, not a blank to hide: those cases
            // sort last and still need clearing.
            valueGetter: (p) =>
              formatIstDateTime(p.row.next_booking_start, "Scheduling Pending"),
          },
          {
            field: "next_review_date",
            headerName: "Review",
            width: 150,
            sortable: false,
            // Safe to decide in the browser because it judges one row against today.
            // A filter or a sort would be judging a truncated page, which is the
            // thing OtCases.js warns against.
            renderCell: (p) =>
              isReviewDue(p.row, istDayKey(new Date())) ? (
                <Chip size="small" color="warning" label="Re-Evaluation Due" />
              ) : (
                p.row.next_review_date || "—"
              ),
          },
          {
            field: "anaesthetist_name",
            headerName: "Anaesthetist",
            flex: 1,
            sortable: false,
            valueGetter: (p) => p.row.anaesthetist_name || "—",
          },
          {
            field: "lifecycle_status",
            headerName: "Status",
            width: 150,
            sortable: false,
            renderCell: (p) => <LifecycleChip status={p.row.lifecycle_status} />,
          },
        ]}
      />
      {pacRow && (
        <PreAnesthesiaAssessmentModal
          otCase={pacRow}
          onClose={() => setPacRow(null)}
          onSaved={load}
        />
      )}
    </OtScreen>
  );
}
